import retryFetch from 'fetch-retry';
import { merge } from 'lodash-es';
import {
  CSRF_TOKEN_HEADER,
  CSRF_TOKEN_STORAGE_KEY,
  applyFreshCsrfToken,
  getStoredCsrfToken,
  isAccessTokenExpiredError,
  isInvalidCsrfTokenError,
  safeParseJson,
} from '../csrf';
import environment from '../environment';
import {
  checkOrSetSessionExpiration,
  infoTokenExists,
  refresh,
  refreshIfAccessTokenExpiringSoon,
} from '../oauth/utilities';
import { checkAndUpdateSSOeSession } from '../sso';
import localStorage from '../storage/localStorage';

const isJson = response => {
  const contentType = response.headers.get('Content-Type');
  return contentType && contentType.includes('application/json');
};

// Build a PII/PHI-safe endpoint string for logging in error messages.
// The query string is dropped entirely (GET params may carry PII), and any
// path segment that looks like an identifier or PII (numeric ids, SSNs, dates,
// MVI ICNs, UUIDs, emails, opaque tokens/hashes) is replaced with `:id`.
// Alphabetic route names are preserved even when they embed digits, so real
// vets-api routes such as `form1010_ezr`, `submit5103`, and
// `post911_gi_bill_status` are NOT accidentally obscured. This keeps the
// endpoint useful for debugging while collapsing per-resource cardinality.
// Residual risk: a free-text alphabetic PII segment (e.g. a bare name) with no
// digits/@ cannot be reliably distinguished from a route name and is kept.
// Known blind spot: base64url/JWT-style tokens containing `_` pass through
// unmasked (the `_` exclusion protects snake_case routes); such tokens rarely
// appear as path segments (they are usually in the query string or headers).
export const sanitizeEndpoint = rawUrl => {
  let pathname;
  try {
    // Absolute URL: keep only the path (drops origin + query string) so the
    // output is always a path, even when the URL is not under API_URL.
    pathname = new URL(rawUrl).pathname;
  } catch {
    // Relative URL: strip the API origin (if present) and the query string.
    pathname = (rawUrl || '').split('?')[0].replace(environment.API_URL, '');
  }
  return pathname
    .split('/')
    .map(segment => {
      if (!segment) return segment;
      const looksLikePii =
        /^\d+$/.test(segment) || // numeric ids, phone, SSN w/o dashes
        /^\d[\d.-]*\d$/.test(segment) || // SSN/DOB/dates with separators
        /^\d{10}v\d{6}$/i.test(segment) || // MVI ICN (e.g. 1013730411V123456)
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          segment,
        ) || // UUID
        /^[0-9a-f]{16,}$/i.test(segment) || // long hex hashes / uuid w/o dashes
        segment.includes('@') || // emails
        segment.includes('%40') || // url-encoded emails
        (segment.length >= 20 &&
          /\d/.test(segment) &&
          /[a-z]/i.test(segment) &&
          !segment.includes('_')); // opaque tokens (JWT-ish, FHIR ids)
      return looksLikePii ? ':id' : segment;
    })
    .join('/');
};

/**
 * Returns plain-object headers carrying the CSRF token exactly once. `Headers`
 * accepts any HeadersInit and deletes case variants (some callers send
 * `x-csrf-token`), but it lowercases every name. The token is re-added under
 * the canonical key so a later spread of `'X-CSRF-Token'` overwrites it,
 * rather than leaving two keys that fetch would join into one invalid value.
 */
const withCsrfHeader = (headers, token) => {
  const merged = new Headers(headers);
  merged.delete(CSRF_TOKEN_HEADER);
  return { ...Object.fromEntries(merged), [CSRF_TOKEN_HEADER]: token };
};

// `settings` is the same request-options object fetch-retry reuses for every
// retry attempt, so mutating settings.headers here (e.g. with a refreshed
// CSRF token) takes effect on the retried request.
const buildRetryOn = settings => {
  let tokenRefreshed = false;
  let csrfRetried = false;

  return async (attempt, error, response) => {
    if (error || response.status !== 403) return false;

    const errorResponse = safeParseJson(await response.clone().text());

    if (
      !tokenRefreshed &&
      isAccessTokenExpiredError(errorResponse) &&
      infoTokenExists()
    ) {
      tokenRefreshed = true;
      await refresh({ type: sessionStorage.getItem('serviceName') });

      return true;
    }

    if (!csrfRetried && isInvalidCsrfTokenError(errorResponse)) {
      csrfRetried = true;
      const freshCsrfToken = applyFreshCsrfToken(
        response.headers.get.bind(response.headers),
      );
      if (!freshCsrfToken) return false;

      // Mutation is required: fetch-retry reuses this same settings object for the retry.
      // eslint-disable-next-line no-param-reassign
      settings.headers = withCsrfHeader(settings.headers, freshCsrfToken);

      return true;
    }

    return false;
  };
};

export function handleSessionUpdates(response) {
  const apiURL = environment.API_URL;
  if (response.url.includes(apiURL)) {
    /**
     * Sets sessionExpiration
     * SAML - Response headers `X-Session-Expiration`
     * OAuth - Cookie set by response
     * */
    checkOrSetSessionExpiration(response);

    // SSOe session is independent of vets-api, and must be kept alive for cross-session continuity
    if (response.ok || response.status === 304) {
      checkAndUpdateSSOeSession();
    }
  }
}

let globalHeaders = {};

/**
 * Sets headers that apiRequest adds to every request until cleared. Explicit
 * per-request headers still take precedence. Pass `{}` (or call
 * clearGlobalApiHeaders) to remove them.
 */
export const setGlobalApiHeaders = headers => {
  globalHeaders = { ...headers };
};

export const clearGlobalApiHeaders = () => {
  globalHeaders = {};
};

const CSRF_SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
let csrfBootstrap = null;

/**
 * Fetches a CSRF token for a session that has none, so its first mutation is
 * not rejected. Concurrent callers share one request. apiRequest stores the
 * X-CSRF-Token header from every response, and HEAD is a safe method, so this
 * cannot trigger another bootstrap. Resolves to the token, or null on failure
 * — the reactive retry in buildRetryOn is the backstop.
 */
const bootstrapCsrfToken = () => {
  if (!csrfBootstrap) {
    // eslint-disable-next-line no-use-before-define
    csrfBootstrap = apiRequest('/csrf_token', { method: 'HEAD' })
      .then(() => getStoredCsrfToken())
      .catch(() => null)
      .finally(() => {
        csrfBootstrap = null;
      });
  }
  return csrfBootstrap;
};

export async function fetchAndUpdateSessionExpiration(url, settings) {
  // use regular fetch if stubbed by sinon or cypress
  if (fetch.isSinonProxy) {
    return fetch(url, settings);
  }

  /**
   * Proactive refresh:
   * If OAuth access token is within 30s of expiring, refresh BEFORE making API request.
   * This reduces the chance of an access token expiring "in flight".
   */
  const apiURL = environment.API_URL;
  const isApiRequest = url.includes(apiURL);
  const isTestEnv = !!window.Mocha;
  const serviceName = sessionStorage.getItem('serviceName');
  const csrfTokenStored = getStoredCsrfToken();

  if (isApiRequest && !isTestEnv && infoTokenExists() && serviceName) {
    try {
      await refreshIfAccessTokenExpiringSoon({
        thresholdSeconds: 30,
        type: serviceName,
      });
    } catch (e) {
      // Intentionally swallow refresh errors to avoid blocking API requests
    }
  }

  const originalFetch = fetch;
  const _fetch = retryFetch(originalFetch);

  const mergedSettings = { ...settings };

  // Fetch a CSRF token before a session's first mutation instead of letting
  // vets-api reject it. Checks run cheapest-first; the storage read is last.
  // Excludes Cypress too (unlike isTestEnv above), since e2e specs don't mock
  // this bootstrap request. Can't use environment.isTest() here: it also
  // checks process.env.NODE_ENV, which stays 'test' for the whole Mocha run,
  // so it would ignore the `window.Mocha = false` specs use to exercise this.
  const method = (mergedSettings.method || 'GET').toUpperCase();
  if (
    isApiRequest &&
    !isTestEnv &&
    !window.Cypress &&
    !CSRF_SAFE_METHODS.has(method) &&
    !csrfTokenStored
  ) {
    const token = await bootstrapCsrfToken();
    if (token) {
      mergedSettings.headers = withCsrfHeader(mergedSettings.headers, token);
    }
  }

  if (!window.Mocha) {
    mergedSettings.retryOn = buildRetryOn(mergedSettings);
  }

  const response = await _fetch(url, mergedSettings);
  handleSessionUpdates(response);
  return response;
}

/**
 *
 * @param {string} resource - The URL to fetch. If it starts with a leading "/"
 * it will be appended to the baseUrl. Otherwise, it will be used as an absolute
 * URL.
 * @param {Object} [{}] optionalSettings - Custom settings you want to apply to
 * the fetch request. These will be merged with, and potentially override, the
 * default settings. Set a header to `null` to omit a default header entirely
 * (e.g. `{ headers: { 'X-Key-Inflection': null } }`).
 * @param {Function} **(DEPRECATED)** success - Callback to execute after successfully resolving
 * the initial fetch request. Prefer using a promise chain instead.
 * @param {Function} **(DEPRECATED)** error - Callback to execute if the fetch fails to resolve.
 * Prefer using a promise chain instead.
 * @param {Object} [env=environment] - **Environment configuration object** used to determine
 * whether the code is running in production or non-production mode. If no environment object is provided, the function defaults to using the
 * global `environment` object.
 */
export function apiRequest(
  resource,
  optionalSettings,
  success,
  error,
  env = environment,
) {
  const apiVersion = (optionalSettings && optionalSettings.apiVersion) || 'v0';
  const baseUrl = `${environment.API_URL}/${apiVersion}`;
  const url = resource[0] === '/' ? [baseUrl, resource].join('') : resource;
  const csrfTokenStored = getStoredCsrfToken();
  const isProd = env.isProduction();

  if (success) {
    // eslint-disable-next-line no-console
    console.warn(
      'the "success" callback has been deprecated, please use a promise chain instead',
    );
  }

  if (error) {
    // eslint-disable-next-line no-console
    console.warn(
      'the "error" callback has been deprecated, please use a promise chain instead',
    );
  }

  const defaultSettings = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'X-Key-Inflection': 'camel',
      'Source-App-Name': window.appName,
      ...globalHeaders,
      [CSRF_TOKEN_HEADER]: csrfTokenStored,
    },
  };
  const settings = merge(defaultSettings, optionalSettings);

  // lodash `merge` can override a default header but never remove one, so a
  // header explicitly set to `null` is treated as an opt-out and dropped.
  // e.g. `{ headers: { 'X-Key-Inflection': null } }` sends the request without
  // it, so vets-api (OliveBranch) leaves the request/response keys as-is.
  Object.keys(settings.headers || {}).forEach(header => {
    if (settings.headers[header] === null) delete settings.headers[header];
  });

  return fetchAndUpdateSessionExpiration(url, settings)
    .catch(err => {
      // Enrich network/transport failures (e.g. "Failed to fetch" / "Load
      // failed") with the request endpoint so it is discoverable in Datadog
      // RUM (these errors otherwise reach RUM with no URL context, collapsing
      // every app's failures into one undebuggable bucket).
      //
      // IMPORTANT: we do NOT mutate `err.message`. Many apps consume the raw
      // message directly (e.g. secure-messaging surfaces it as an error
      // `title`) and assert on it exactly, so changing it is a breaking
      // contract change. Instead we attach structured `url`/`method` props and
      // append the endpoint to `err.stack`, which RUM's addError captures and
      // makes searchable via `@error.stack`, without affecting message-based
      // grouping or any app that reads `err.message`. The endpoint is
      // PII/PHI-sanitized (query string dropped, ids obscured) before logging.
      if (err instanceof Error && !err.endpointAppended) {
        const method = (settings.method || 'GET').toUpperCase();
        const endpoint = sanitizeEndpoint(url);
        const enriched = err;
        // Always use the sanitized endpoint so a pre-existing (possibly raw)
        // `err.url` can never bypass PII/PHI sanitization.
        enriched.url = endpoint;
        enriched.method = err.method || method;
        enriched.endpointAppended = true;
        if (typeof err.stack === 'string') {
          enriched.stack = `${err.stack}\n    at apiRequest [${method} ${endpoint}]`;
        }
      }
      return Promise.reject(err);
    })
    .then(response => {
      const data = isJson(response)
        ? response.json()
        : Promise.resolve(response);

      // Get CSRF Token from API header
      const csrfToken = response.headers.get(CSRF_TOKEN_HEADER);

      if (csrfToken && csrfToken !== csrfTokenStored) {
        localStorage.setItem(CSRF_TOKEN_STORAGE_KEY, csrfToken);
      }

      if (response.ok || response.status === 304) {
        return data;
      }

      if (isProd) {
        const { pathname } = window.location;

        const shouldRedirectToSessionExpired =
          response.status === 401 &&
          !pathname.includes('auth/login/callback') &&
          sessionStorage.getItem('shouldRedirectExpiredSession') === 'true' &&
          !pathname.includes('/terms-of-use/declined');

        if (shouldRedirectToSessionExpired) {
          sessionStorage.removeItem('shouldRedirectExpiredSession');
          window.location = '/?next=loginModal&status=session_expired';
        }
      }

      return data.then(Promise.reject.bind(Promise));
    })
    .then(success)
    .catch(error);
}
